import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import { useRole } from '../hooks/useRole';
import { licensePlanService } from '../api';
import { Button, ThemedText } from '../components';
import { PageContainer, PageHeaderRow } from '../components/PageStyles';
import { ToggleSwitch, ToggleInput, ToggleSlider } from '../components/ToggleStyles';
import {
  ArrowLeft, FileText, Server,
  DollarSign, TrendingUp, Sparkles,
} from 'lucide-react';

// --- Helpers ---

const STORAGE_UNITS = [
  { label: 'MB', multiplier: 1048576 },
  { label: 'GB', multiplier: 1073741824 },
];

const formatBytes = (bytes) => {
  if (bytes == null || bytes === 0) return '0 MB';
  if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(1)} GB`;
  return `${(bytes / 1048576).toFixed(0)} MB`;
};

const bytesToUnit = (bytes) => {
  if (!bytes) return { value: '', unit: 'GB' };
  if (bytes >= 1073741824 && bytes % 1073741824 === 0)
    return { value: String(bytes / 1073741824), unit: 'GB' };
  return { value: String(bytes / 1048576), unit: 'MB' };
};

const unitToBytes = (value, unit) => {
  if (!value) return '';
  const m = STORAGE_UNITS.find(u => u.label === unit);
  return String(Number(value) * (m?.multiplier || 1073741824));
};

// --- Limit fields config ---

const LIMIT_FIELDS = [
  { group: 'core', label: 'Users', field: 'maxUsers', overageField: 'overageUserCost' },
  { group: 'core', label: 'Forms', field: 'maxFormSchemas', overageField: 'overageFormCost' },
  { group: 'core', label: 'Submissions / month', field: 'maxSubmissionsPerMonth', overageField: 'overageSubmissionCost', step: '0.001', placeholder: '0.000' },
  { group: 'core', label: 'Total submissions cap', field: 'maxTotalSubmissions', hint: 'Lifetime cap across all months.' },
  { group: 'core', label: 'Plan duration (days)', field: 'durationDays', placeholder: 'No expiry' },
  { group: 'core', label: 'Report templates', field: 'maxPdfTemplates', overageField: 'overagePdfTemplateCost' },
  { group: 'core', label: 'Storage', field: 'maxStorageBytes', overageField: 'overageStorageCostPerGB', isStorage: true, overageLabel: '$ / extra GB' },
  { group: 'core', label: 'AI requests / month', field: 'maxAIGenerationsPerMonth', overageField: 'overageAIGenerationCost', icon: Sparkles },
  { group: 'operations', label: 'Customers', field: 'maxCustomers', overageField: 'overageCustomerCost' },
  { group: 'operations', label: 'Work orders', field: 'maxWorkOrders', overageField: 'overageWorkOrderCost' },
  { group: 'operations', label: 'Locations', field: 'maxLocations', overageField: 'overageLocationCost' },
];

// --- Styled Components ---

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.xs};
  background: none;
  border: none;
  color: ${p => p.theme.colors.tint};
  cursor: pointer;
  font-size: ${p => p.theme.typography.sizes.default};
  padding: 0;
  &:hover { text-decoration: underline; }
`;

const SectionCard = styled.div`
  background-color: ${p => p.theme.colors.backgroundSecondary};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl};
  margin-bottom: ${p => p.theme.spacing.lg};
  box-shadow: ${p => p.theme.shadows.sm || '0 1px 3px rgba(0,0,0,0.08)'};
`;

const SectionTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  margin-bottom: ${p => p.theme.spacing.xs};
`;

const SectionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${p => p.theme.borderRadius.md};
  background-color: ${p => p.$color || p.theme.colors.tint}12;
  color: ${p => p.$color || p.theme.colors.tint};
  flex-shrink: 0;
`;

const SectionTitle = styled.h3`
  font-size: ${p => p.theme.typography.sizes.default};
  font-weight: 700;
  color: ${p => p.theme.colors.text};
  margin: 0;
  letter-spacing: 0.3px;
`;

const SectionDescription = styled.p`
  font-size: ${p => p.theme.typography.sizes.small};
  color: ${p => p.theme.colors.muted};
  margin: 0 0 ${p => p.theme.spacing.lg} 0;
  padding-left: 44px;
  line-height: 1.5;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: ${p => p.$cols || '1fr 1fr'};
  gap: ${p => p.theme.spacing.md};
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.spacing.xs};
  margin-bottom: ${p => p.theme.spacing.md};
`;

const Label = styled.label`
  font-weight: ${p => p.theme.typography.weights.semiBold};
  color: ${p => p.theme.colors.text};
  font-size: ${p => p.theme.typography.sizes.small};
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Required = styled.span`
  color: ${p => p.theme.colors.tint};
`;

const HintText = styled.div`
  font-size: 12px;
  color: ${p => p.theme.colors.muted};
  margin-top: 2px;
  line-height: 1.4;
`;

const StyledInput = styled.input`
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  background-color: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.text};
  font-size: ${p => p.theme.typography.sizes.default};
  min-height: 40px;
  box-sizing: border-box;
  transition: border-color 0.15s ease;
  &:focus {
    outline: none;
    border-color: ${p => p.theme.colors.tint};
    box-shadow: 0 0 0 2px ${p => p.theme.colors.tint}20;
  }
  &::placeholder { color: ${p => p.theme.colors.muted}; }
`;

const StyledTextarea = styled.textarea`
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  background-color: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.text};
  font-size: ${p => p.theme.typography.sizes.default};
  resize: vertical;
  min-height: 60px;
  font-family: inherit;
  box-sizing: border-box;
  transition: border-color 0.15s ease;
  &:focus {
    outline: none;
    border-color: ${p => p.theme.colors.tint};
    box-shadow: 0 0 0 2px ${p => p.theme.colors.tint}20;
  }
  &::placeholder { color: ${p => p.theme.colors.muted}; }
`;

const StorageRow = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.sm};
  align-items: center;
`;

const UnitSelect = styled.select`
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  background-color: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.text};
  font-size: ${p => p.theme.typography.sizes.default};
  min-height: 40px;
  cursor: pointer;
  &:focus {
    outline: none;
    border-color: ${p => p.theme.colors.tint};
  }
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.md};
`;

const ToggleLabel = styled.span`
  font-size: ${p => p.theme.typography.sizes.small};
  color: ${p => p.theme.colors.text};
  font-weight: ${p => p.theme.typography.weights.semiBold};
`;

const OverageBannerWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${p => p.theme.colors.backgroundSecondary};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.md} ${p => p.theme.spacing.xl};
  margin-bottom: ${p => p.theme.spacing.lg};
`;

const OverageBannerLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
`;

const OverageBannerIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: ${p => p.theme.borderRadius.md};
  background-color: #9b59b612;
  color: #9b59b6;
  flex-shrink: 0;
`;

const OverageBannerLabel = styled.span`
  font-weight: ${p => p.theme.typography.weights.semiBold};
  font-size: ${p => p.theme.typography.sizes.small};
  color: ${p => p.theme.colors.text};
`;

const OverageBannerDesc = styled.span`
  font-size: ${p => p.theme.typography.sizes.small};
  color: ${p => p.theme.colors.muted};
  margin-left: ${p => p.theme.spacing.sm};
`;

const ColumnHeaders = styled.div`
  display: grid;
  grid-template-columns: ${p => p.$cols};
  gap: ${p => p.theme.spacing.md};
  padding-bottom: ${p => p.theme.spacing.sm};
  margin-bottom: ${p => p.theme.spacing.sm};
  border-bottom: 1px solid ${p => p.theme.colors.border};
`;

const ColumnHeaderText = styled.span`
  font-size: 12px;
  font-weight: ${p => p.theme.typography.weights.semiBold};
  color: ${p => p.theme.colors.muted};
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const SubSectionLabel = styled.div`
  font-size: 12px;
  font-weight: ${p => p.theme.typography.weights.semiBold};
  color: ${p => p.theme.colors.muted};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-top: ${p => p.theme.spacing.md};
  margin-bottom: ${p => p.theme.spacing.sm};
  &:first-child { margin-top: 0; }
`;

const LimitRowWrap = styled.div`
  display: grid;
  grid-template-columns: ${p => p.$cols};
  gap: ${p => p.theme.spacing.md};
  align-items: start;
  padding: ${p => p.theme.spacing.sm} 0;
  border-bottom: 1px solid ${p => p.theme.colors.border}40;
  &:last-child { border-bottom: none; }
`;

const LimitLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: ${p => p.theme.typography.weights.semiBold};
  font-size: ${p => p.theme.typography.sizes.small};
  color: ${p => p.theme.colors.text};
  min-height: 40px;
`;

const CostInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const CostPrefix = styled.span`
  position: absolute;
  left: 12px;
  font-size: ${p => p.theme.typography.sizes.default};
  color: ${p => p.theme.colors.muted};
  pointer-events: none;
`;

const CostInput = styled(StyledInput)`
  padding-left: 24px;
  width: 100%;
`;

const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${p => p.theme.spacing.md};
  margin-bottom: ${p => p.theme.spacing.xl};
`;

// --- Initial Form ---

const INITIAL_FORM = {
  name: '', planCode: '', description: '',
  displayOrder: '', isActive: true,
  maxUsers: '', maxFormSchemas: '', maxSubmissionsPerMonth: '',
  maxTotalSubmissions: '', durationDays: '',
  maxPdfTemplates: '', maxStorageBytes: '',
  maxAIGenerationsPerMonth: '',
  maxCustomers: '', maxWorkOrders: '', maxLocations: '',
  allowOverage: false,
  overageUserCost: '', overageFormCost: '', overageSubmissionCost: '',
  overagePdfTemplateCost: '', overageStorageCostPerGB: '',
  overageAIGenerationCost: '',
  overageWorkOrderCost: '', overageCustomerCost: '', overageLocationCost: '',
  monthlyPrice: '', annualPrice: '',
};

// --- Component ---

const CreateLicensePlanPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const { isSuperAdmin } = useRole();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState({ ...INITIAL_FORM });
  const [saving, setSaving] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(false);

  // Storage unit state — derived from initial bytes value
  const [storageValue, setStorageValue] = useState('');
  const [storageUnit, setStorageUnit] = useState('GB');

  useEffect(() => {
    if (isEditMode) {
      loadPlan();
    }
  }, [id]); // eslint-disable-line

  const loadPlan = async () => {
    try {
      setLoadingPlan(true);
      const response = await licensePlanService.getPlanById(id);
      const plan = response?.data || response;
      const { value: sv, unit: su } = bytesToUnit(plan.maxStorageBytes);
      setStorageValue(sv);
      setStorageUnit(su);
      setFormData({
        name: plan.name || '',
        planCode: plan.planCode || '',
        description: plan.description || '',
        displayOrder: plan.displayOrder ?? '',
        isActive: plan.isActive ?? true,
        maxUsers: plan.maxUsers ?? '',
        maxFormSchemas: plan.maxFormSchemas ?? '',
        maxSubmissionsPerMonth: plan.maxSubmissionsPerMonth ?? '',
        maxTotalSubmissions: plan.maxTotalSubmissions ?? '',
        durationDays: plan.durationDays ?? '',
        maxPdfTemplates: plan.maxPdfTemplates ?? '',
        maxStorageBytes: plan.maxStorageBytes ?? '',
        maxAIGenerationsPerMonth: plan.maxAIGenerationsPerMonth ?? '',
        maxCustomers: plan.maxCustomers ?? '',
        maxWorkOrders: plan.maxWorkOrders ?? '',
        maxLocations: plan.maxLocations ?? '',
        allowOverage: plan.allowOverage ?? false,
        overageUserCost: plan.overageUserCost ?? '',
        overageFormCost: plan.overageFormCost ?? '',
        overageSubmissionCost: plan.overageSubmissionCost ?? '',
        overagePdfTemplateCost: plan.overagePdfTemplateCost ?? '',
        overageStorageCostPerGB: plan.overageStorageCostPerGB ?? '',
        overageAIGenerationCost: plan.overageAIGenerationCost ?? '',
        overageWorkOrderCost: plan.overageWorkOrderCost ?? '',
        overageCustomerCost: plan.overageCustomerCost ?? '',
        overageLocationCost: plan.overageLocationCost ?? '',
        monthlyPrice: plan.monthlyPrice ?? '',
        annualPrice: plan.annualPrice ?? '',
      });
    } catch (err) {
      console.error('Failed to load plan:', err);
      toast.error('Failed to load license plan');
      navigate('/license-plans');
    } finally {
      setLoadingPlan(false);
    }
  };

  const updateField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleStorageChange = (val, unit) => {
    setStorageValue(val);
    setStorageUnit(unit);
    updateField('maxStorageBytes', unitToBytes(val, unit));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { toast.error('Plan name is required'); return; }
    if (!formData.planCode.trim()) { toast.error('Plan code is required'); return; }

    try {
      setSaving(true);
      const toNullableInt = (v) => (v === '' || v === null || v === undefined) ? null : Number(v);
      const toNullableDecimal = (v) => (v === '' || v === null || v === undefined) ? null : Number(v);

      const payload = {
        name: formData.name,
        planCode: formData.planCode,
        description: formData.description || null,
        displayOrder: toNullableInt(formData.displayOrder),
        isActive: formData.isActive,
        maxUsers: toNullableInt(formData.maxUsers),
        maxFormSchemas: toNullableInt(formData.maxFormSchemas),
        maxSubmissionsPerMonth: toNullableInt(formData.maxSubmissionsPerMonth),
        maxTotalSubmissions: toNullableInt(formData.maxTotalSubmissions),
        durationDays: toNullableInt(formData.durationDays),
        maxPdfTemplates: toNullableInt(formData.maxPdfTemplates),
        maxStorageBytes: toNullableInt(formData.maxStorageBytes),
        maxAIGenerationsPerMonth: toNullableInt(formData.maxAIGenerationsPerMonth),
        maxCustomers: toNullableInt(formData.maxCustomers),
        maxWorkOrders: toNullableInt(formData.maxWorkOrders),
        maxLocations: toNullableInt(formData.maxLocations),
        allowOverage: formData.allowOverage,
        overageUserCost: toNullableDecimal(formData.overageUserCost),
        overageFormCost: toNullableDecimal(formData.overageFormCost),
        overageSubmissionCost: toNullableDecimal(formData.overageSubmissionCost),
        overagePdfTemplateCost: toNullableDecimal(formData.overagePdfTemplateCost),
        overageStorageCostPerGB: toNullableDecimal(formData.overageStorageCostPerGB),
        overageAIGenerationCost: toNullableDecimal(formData.overageAIGenerationCost),
        overageWorkOrderCost: toNullableDecimal(formData.overageWorkOrderCost),
        overageCustomerCost: toNullableDecimal(formData.overageCustomerCost),
        overageLocationCost: toNullableDecimal(formData.overageLocationCost),
        monthlyPrice: toNullableDecimal(formData.monthlyPrice),
        annualPrice: toNullableDecimal(formData.annualPrice),
      };

      if (isEditMode) {
        await licensePlanService.updatePlan(id, payload);
        toast.success('Plan updated successfully');
      } else {
        await licensePlanService.createPlan(payload);
        toast.success('Plan created successfully');
      }
      navigate('/license-plans');
    } catch (err) {
      console.error('Failed to save plan:', err);
      toast.error(err.response?.data?.message || 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  const gridCols = formData.allowOverage ? '180px 1fr 1fr' : '180px 1fr';

  const renderLimitRow = (item) => {
    const IconComp = item.icon;
    const hasOverage = formData.allowOverage && item.overageField;

    return (
      <LimitRowWrap key={item.field} $cols={gridCols} theme={theme}>
        <LimitLabel theme={theme}>
          {IconComp && <IconComp size={13} />}
          {item.label}
        </LimitLabel>

        {item.isStorage ? (
          <div>
            <StorageRow theme={theme}>
              <StyledInput
                theme={theme}
                type="number"
                min="0"
                value={storageValue}
                onChange={e => handleStorageChange(e.target.value, storageUnit)}
                placeholder="Unlimited"
                style={{ flex: 1 }}
              />
              <UnitSelect
                theme={theme}
                value={storageUnit}
                onChange={e => handleStorageChange(storageValue, e.target.value)}
              >
                {STORAGE_UNITS.map(u => (
                  <option key={u.label} value={u.label}>{u.label}</option>
                ))}
              </UnitSelect>
            </StorageRow>
            {formData.maxStorageBytes && (
              <HintText theme={theme}>{formatBytes(Number(formData.maxStorageBytes))} total file storage.</HintText>
            )}
          </div>
        ) : (
          <div>
            <StyledInput
              theme={theme}
              type="number"
              min={item.field === 'durationDays' ? '1' : '0'}
              value={formData[item.field]}
              onChange={e => updateField(item.field, e.target.value)}
              placeholder={item.placeholder || 'Unlimited'}
              style={{ width: '100%' }}
            />
            {item.hint && <HintText theme={theme}>{item.hint}</HintText>}
          </div>
        )}

        {formData.allowOverage && (
          hasOverage ? (
            <CostInputWrapper>
              <CostPrefix theme={theme}>$</CostPrefix>
              <CostInput
                theme={theme}
                type="number"
                step={item.step || '0.01'}
                min="0"
                value={formData[item.overageField]}
                onChange={e => updateField(item.overageField, e.target.value)}
                placeholder={item.placeholder || '0.00'}
              />
            </CostInputWrapper>
          ) : (
            <div />
          )
        )}
      </LimitRowWrap>
    );
  };

  const coreFields = LIMIT_FIELDS.filter(f => f.group === 'core');
  const opsFields = LIMIT_FIELDS.filter(f => f.group === 'operations');

  if (!isSuperAdmin()) {
    return (
      <Layout currentPath="/license-plans">
        <PageContainer theme={theme}>
          <ThemedText type="title">Access Denied</ThemedText>
          <ThemedText>You do not have permission to access this page.</ThemedText>
        </PageContainer>
      </Layout>
    );
  }

  if (loadingPlan) {
    return (
      <Layout currentPath="/license-plans">
        <PageContainer theme={theme}>
          <ThemedText>Loading plan...</ThemedText>
        </PageContainer>
      </Layout>
    );
  }

  return (
    <Layout currentPath="/license-plans">
      <PageContainer theme={theme}>
        <PageHeaderRow theme={theme}>
          <div>
            <BackButton theme={theme} onClick={() => navigate('/license-plans')}>
              <ArrowLeft size={14} /> Back to License Plans
            </BackButton>
            <ThemedText type="title" style={{ marginTop: 8 }}>
              {isEditMode ? 'Edit License Plan' : 'New License Plan'}
            </ThemedText>
          </div>
        </PageHeaderRow>

        <form onSubmit={handleSave}>
          {/* ── Section 1: Plan Identity ── */}
          <SectionCard theme={theme}>
            <SectionTitleRow theme={theme}>
              <SectionIcon theme={theme} $color={theme.colors.primary}>
                <FileText size={16} />
              </SectionIcon>
              <SectionTitle theme={theme}>Plan Identity</SectionTitle>
            </SectionTitleRow>
            <SectionDescription theme={theme}>
              The name and code that identify this plan. Code should be short and unique (e.g. FREE, PRO, ENTERPRISE).
            </SectionDescription>

            <FormRow theme={theme} $cols="1fr 1fr 1fr">
              <FormGroup theme={theme}>
                <Label theme={theme}>Name <Required theme={theme}>*</Required></Label>
                <StyledInput theme={theme} value={formData.name} onChange={e => updateField('name', e.target.value)} placeholder="e.g. Professional" required />
              </FormGroup>
              <FormGroup theme={theme}>
                <Label theme={theme}>Plan Code <Required theme={theme}>*</Required></Label>
                <StyledInput theme={theme} value={formData.planCode} onChange={e => updateField('planCode', e.target.value)} placeholder="e.g. PRO" style={{ textTransform: 'uppercase' }} required />
              </FormGroup>
              <FormGroup theme={theme}>
                <Label theme={theme}>Display Order</Label>
                <StyledInput theme={theme} type="number" min="0" value={formData.displayOrder} onChange={e => updateField('displayOrder', e.target.value)} placeholder="0" />
                <HintText theme={theme}>Lower numbers appear first.</HintText>
              </FormGroup>
            </FormRow>
            <FormGroup theme={theme}>
              <Label theme={theme}>Description</Label>
              <StyledTextarea theme={theme} value={formData.description} onChange={e => updateField('description', e.target.value)} placeholder="A brief description shown to admins when comparing plans" />
            </FormGroup>
            <ToggleRow theme={theme}>
              <ToggleSwitch>
                <ToggleInput type="checkbox" checked={formData.isActive} onChange={e => updateField('isActive', e.target.checked)} />
                <ToggleSlider $checked={formData.isActive} />
              </ToggleSwitch>
              <ToggleLabel theme={theme}>{formData.isActive ? 'Active' : 'Inactive'}</ToggleLabel>
              <HintText theme={theme} style={{ marginTop: 0 }}>Inactive plans can't be assigned to new organizations.</HintText>
            </ToggleRow>
          </SectionCard>

          {/* ── Overage Banner ── */}
          <OverageBannerWrap theme={theme}>
            <OverageBannerLeft theme={theme}>
              <OverageBannerIcon>
                <TrendingUp size={14} />
              </OverageBannerIcon>
              <OverageBannerLabel theme={theme}>Overage Billing</OverageBannerLabel>
              <OverageBannerDesc theme={theme}>
                Allow organizations to exceed limits and get billed per extra item.
              </OverageBannerDesc>
            </OverageBannerLeft>
            <ToggleRow theme={theme}>
              <ToggleSwitch>
                <ToggleInput type="checkbox" checked={formData.allowOverage} onChange={e => updateField('allowOverage', e.target.checked)} />
                <ToggleSlider $checked={formData.allowOverage} />
              </ToggleSwitch>
            </ToggleRow>
          </OverageBannerWrap>

          {/* ── Section 3: Limits ── */}
          <SectionCard theme={theme}>
            <SectionTitleRow theme={theme}>
              <SectionIcon theme={theme} $color={theme.colors.tint}>
                <Server size={16} />
              </SectionIcon>
              <SectionTitle theme={theme}>Limits</SectionTitle>
            </SectionTitleRow>
            <SectionDescription theme={theme}>
              Set the maximum resources available to organizations on this plan. Leave any field blank to allow unlimited usage.
            </SectionDescription>

            <ColumnHeaders $cols={gridCols} theme={theme}>
              <ColumnHeaderText theme={theme}>Resource</ColumnHeaderText>
              <ColumnHeaderText theme={theme}>Limit</ColumnHeaderText>
              {formData.allowOverage && (
                <ColumnHeaderText theme={theme}>Overage cost per unit</ColumnHeaderText>
              )}
            </ColumnHeaders>

            <SubSectionLabel theme={theme}>Core Resources</SubSectionLabel>
            {coreFields.map(renderLimitRow)}

            <SubSectionLabel theme={theme}>Operations</SubSectionLabel>
            {opsFields.map(renderLimitRow)}
          </SectionCard>

          {/* ── Section 4: Pricing ── */}
          <SectionCard theme={theme}>
            <SectionTitleRow theme={theme}>
              <SectionIcon theme={theme} $color="#27ae60">
                <DollarSign size={16} />
              </SectionIcon>
              <SectionTitle theme={theme}>Pricing</SectionTitle>
            </SectionTitleRow>
            <SectionDescription theme={theme}>
              Set the subscription prices. Leave blank if pricing is handled off-platform or requires a sales conversation.
            </SectionDescription>

            <FormRow theme={theme}>
              <FormGroup theme={theme}>
                <Label theme={theme}>Monthly price ($)</Label>
                <StyledInput theme={theme} type="number" step="0.01" min="0" value={formData.monthlyPrice} onChange={e => updateField('monthlyPrice', e.target.value)} placeholder="Contact Sales" />
              </FormGroup>
              <FormGroup theme={theme}>
                <Label theme={theme}>Annual price ($)</Label>
                <StyledInput theme={theme} type="number" step="0.01" min="0" value={formData.annualPrice} onChange={e => updateField('annualPrice', e.target.value)} placeholder="Contact Sales" />
                {formData.monthlyPrice && formData.annualPrice && (
                  <HintText theme={theme}>
                    {Number(formData.annualPrice) < Number(formData.monthlyPrice) * 12
                      ? `Saves ${Math.round((1 - Number(formData.annualPrice) / (Number(formData.monthlyPrice) * 12)) * 100)}% vs. monthly`
                      : 'No discount vs. monthly'}
                  </HintText>
                )}
              </FormGroup>
            </FormRow>
          </SectionCard>

          <FooterActions theme={theme}>
            <Button type="button" variant="secondary" onClick={() => navigate('/license-plans')}>Cancel</Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving...' : (isEditMode ? 'Update Plan' : 'Create Plan')}
            </Button>
          </FooterActions>
        </form>
      </PageContainer>
    </Layout>
  );
};

export default CreateLicensePlanPage;
export { CreateLicensePlanPage };
